import {
  LockOutlined,
  RightCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Card, Form, Input, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import AnimatedBackground from "../../components/common/AnimatedBackground";
import AnimatedLogo from "../../components/common/AnimatedLogo";
import Loading from "../../components/common/Loading";
import { AppDispatch, RootState } from "../../store/index";
import { fetchMe, login } from "../../store/slices/authSlice";
import { fetchWarehouseLocations } from "../../store/slices/warehouseSlice";
import { notify } from "../../utils/toast";
const Login: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleFinish = () => {
    form
      .validateFields()
      .then(async (values) => {
        setLoading(true);
        try {
          const res = await dispatch(login(values)).unwrap();
          if (res) {
            await dispatch(fetchMe());
            await dispatch(fetchWarehouseLocations());
            navigate("/dashboard");
            notify.success("Đăng nhập thành công");
          }
        } catch (error: any) {
          if (error.statusCode === 403) {
            navigate("/blocked");
          } else {
            notify.error(error.message);
          }
        } finally {
          setLoading(false);
        }
      })
      .catch((error) => {});
  };

  return (
    <>
      {loading && <Loading show={loading} />}
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 flex items-center justify-center p-6 relative overflow-hidden transition-all duration-500">
        <AnimatedBackground variant="login" />

        <div className="relative z-10 w-full max-w-md">
          <div
            className={`text-center mb-8 transform transition-all duration-1000 ${
              isVisible
                ? "translate-y-0 opacity-100 scale-100"
                : "translate-y-10 opacity-0 scale-95"
            }`}
          >
            <AnimatedLogo size="large" showStar={true} className="mb-4" />
          </div>

          <div
            className={`transform transition-all duration-1000 delay-300 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border-0 shadow-2xl rounded-3xl overflow-hidden">
              <div className="p-4">
                <div className="text-center mb-8">
                  <Typography.Title
                    level={3}
                    className="mb-2 text-gray-800 dark:text-white"
                    style={{
                      fontFamily: "Chakra Petch, sans-serif",
                      fontWeight: "bold",
                    }}
                  >
                    Đăng nhập
                  </Typography.Title>
                  <Typography.Text
                    className="text-gray-600 dark:text-gray-300"
                    style={{
                      fontFamily: "Chakra Petch, sans-serif",
                    }}
                  >
                    Vui lòng nhập thông tin tài khoản để tiếp tục
                  </Typography.Text>
                </div>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  className="space-y-6"
                >
                  <Form.Item
                    label={
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        Email
                      </span>
                    }
                    name="email"
                    rules={[
                      { required: true, message: "Vui lòng nhập email" },
                      { type: "email", message: "Email không hợp lệ" },
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined className="text-gray-400" />}
                      placeholder="you@example.com"
                      size="large"
                      className="rounded-xl border-gray-200 dark:border-gray-600 hover:border-blue-400 focus:border-blue-500 transition-colors"
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        Mật khẩu
                      </span>
                    }
                    name="password"
                    rules={[
                      { required: true, message: "Vui lòng nhập mật khẩu" },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined className="text-gray-400" />}
                      placeholder="••••••••"
                      size="large"
                      className="rounded-xl border-gray-200 dark:border-gray-600 hover:border-blue-400 focus:border-blue-500 transition-colors"
                    />
                  </Form.Item>

                  <Form.Item className="mb-0">
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      loading={loading}
                      icon={!loading && <RightCircleOutlined />}
                      className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 border-0 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold"
                    >
                      {loading ? "Đang đăng nhập..." : "Đăng nhập"}
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            </Card>
          </div>

          <div
            className={`text-center mt-8 transform transition-all duration-1000 delay-700 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">
              © 2025 Trace-Tec. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
